import { useTranslation } from 'react-i18next';
import type { ClientAnnouncement } from '@/api/announcements/announcementsApi.types';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/common/Icon';
import { formatRelativeTime } from '@/utils/formatRelativeTime';
import { linkifyText } from './linkify';

const RECENT_ANNOUNCEMENT_WINDOW_MILLISECONDS = 7 * 24 * 60 * 60 * 1000;

interface AnnouncementItemProps {
  announcement: ClientAnnouncement;
  senderName: string;
}

export function AnnouncementItem({ announcement, senderName }: AnnouncementItemProps) {
  const { t } = useTranslation('clients');
  const publishedTimestamp = new Date(announcement.publishedAt).getTime();
  const isRecentAnnouncement =
    Date.now() - publishedTimestamp <= RECENT_ANNOUNCEMENT_WINDOW_MILLISECONDS;

  return (
    <article className="py-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="w-6 h-6 rounded-lg bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Megaphone" className="text-[11px]" />
        </span>
        <span className="text-xs font-semibold text-zooindigo">{senderName}</span>
        <span className="text-[11px] text-zoomuted">
          {formatRelativeTime(announcement.publishedAt)}
        </span>
        {isRecentAnnouncement === true ? (
          <Badge tone="accent" size="sm">
            {t('announcements.newBadge')}
          </Badge>
        ) : null}
      </div>
      <h3 className="text-sm font-bold text-zooindigo mt-1.5">{announcement.title}</h3>
      <p className="text-sm text-zooindigo whitespace-pre-wrap mt-1">
        {linkifyText(announcement.body)}
      </p>
    </article>
  );
}
